import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';

import * as api from './contentCalendarAPI.js';

// Async thunks

export const fetchCalendarItems = createAsyncThunk('contentCalendar/fetchItems', async ({ profileId, from, to, types }, { rejectWithValue }) => {
  try {
    const res = await api.getContentCalendar(profileId, from, to, types);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchUpcoming = createAsyncThunk('contentCalendar/fetchUpcoming', async ({ profileId, limit = 5 }, { rejectWithValue }) => {
  try {
    const res = await api.getUpcoming(profileId, limit);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchStats = createAsyncThunk('contentCalendar/fetchStats', async ({ profileId }, { rejectWithValue }) => {
  try {
    const res = await api.getSchedulerStats(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchRecurrencePreview = createAsyncThunk('contentCalendar/fetchRecurrencePreview', async (params, { rejectWithValue }) => {
  try {
    const res = await api.getRecurrencePreview(params);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const createTask = createAsyncThunk('contentCalendar/createTask', async ({ profileId, data }, { rejectWithValue }) => {
  try {
    const res = await api.createScheduledTask(profileId, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const deleteTask = createAsyncThunk('contentCalendar/deleteTask', async ({ profileId, taskId }, { rejectWithValue }) => {
  try {
    await api.deleteScheduledTask(profileId, taskId);
    return taskId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Drag-and-drop handler dispatches this. We update the item optimistically
// (reducer patches items[].ScheduledAt) so the event jumps to its new slot
// the moment the drop completes, instead of waiting for the server round trip.
export const rescheduleTask = createAsyncThunk('contentCalendar/rescheduleTask', async ({ profileId, taskId, scheduledAt, scheduledTimeZone }, { rejectWithValue }) => {
  try {
    await api.rescheduleScheduledTask(profileId, taskId, { ScheduledAt: scheduledAt, ScheduledTimeZone: scheduledTimeZone });
    return { taskId, scheduledAt };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Slice

const contentCalendarSlice = createSlice({
  name: 'contentCalendar',
  initialState: {
    items: [],
    summary: null,
    warnings: [],
    isLoading: false,
    error: null,

    dateRange: { from: null, to: null },
    typeFilter: [],
    platformFilter: [],
    statusFilter: [],

    upcoming: [],
    upcomingLoading: false,

    stats: null,
    statsLoading: false,

    recurrencePreview: [],
    recurrencePreviewLoading: false,

    selectedEvent: null,

    // Bulk-select mode for multi-delete from the calendar
    selectMode: false,
    selectedIds: [],

    // UI toggle for the best-time-to-post overlay (week/day views)
    showBestTimes: false,
  },
  reducers: {
    setDateRange: (state, { payload }) => {
      state.dateRange = payload;
    },
    setTypeFilter: (state, { payload }) => {
      state.typeFilter = payload;
    },
    setPlatformFilter: (state, { payload }) => {
      state.platformFilter = payload;
    },
    setStatusFilter: (state, { payload }) => {
      state.statusFilter = payload;
    },
    clearCalendarFilters: (state) => {
      state.typeFilter = [];
      state.platformFilter = [];
      state.statusFilter = [];
    },
    // Optimistic reschedule: patches items[].ScheduledAt immediately. Paired
    // with `revertReschedule` which restores the old value on server error.
    applyOptimisticReschedule: (state, { payload }) => {
      const { taskId, scheduledAt } = payload;
      const idx = state.items.findIndex((i) => i.Id === taskId);
      if (idx !== -1) state.items[idx].ScheduledAt = scheduledAt;
    },
    revertReschedule: (state, { payload }) => {
      const { taskId, scheduledAt } = payload;
      const idx = state.items.findIndex((i) => i.Id === taskId);
      if (idx !== -1) state.items[idx].ScheduledAt = scheduledAt;
    },
    toggleSelectMode: (state) => {
      state.selectMode = !state.selectMode;
      if (!state.selectMode) state.selectedIds = [];
    },
    setSelectMode: (state, { payload }) => {
      state.selectMode = !!payload;
      if (!state.selectMode) state.selectedIds = [];
    },
    toggleEventSelection: (state, { payload }) => {
      const id = payload;
      const idx = state.selectedIds.indexOf(id);
      if (idx === -1) state.selectedIds.push(id);
      else state.selectedIds.splice(idx, 1);
    },
    clearSelection: (state) => {
      state.selectedIds = [];
    },
    setShowBestTimes: (state, { payload }) => {
      state.showBestTimes = !!payload;
    },
    setSelectedEvent: (state, { payload }) => {
      state.selectedEvent = payload;
    },
    clearSelectedEvent: (state) => {
      state.selectedEvent = null;
    },
    clearRecurrencePreview: (state) => {
      state.recurrencePreview = [];
    },
  },
  extraReducers: (builder) => {
    // Calendar items
    builder
      .addCase(fetchCalendarItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCalendarItems.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.items = payload.Items || [];
        state.summary = payload.Summary || null;
        state.warnings = payload.Warnings || [];
      })
      .addCase(fetchCalendarItems.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      });

    // Upcoming
    builder
      .addCase(fetchUpcoming.pending, (state) => {
        state.upcomingLoading = true;
      })
      .addCase(fetchUpcoming.fulfilled, (state, { payload }) => {
        state.upcomingLoading = false;
        state.upcoming = payload || [];
      })
      .addCase(fetchUpcoming.rejected, (state) => {
        state.upcomingLoading = false;
      });

    // Stats
    builder
      .addCase(fetchStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchStats.fulfilled, (state, { payload }) => {
        state.statsLoading = false;
        state.stats = payload;
      })
      .addCase(fetchStats.rejected, (state) => {
        state.statsLoading = false;
      });

    // Recurrence preview
    builder
      .addCase(fetchRecurrencePreview.pending, (state) => {
        state.recurrencePreviewLoading = true;
      })
      .addCase(fetchRecurrencePreview.fulfilled, (state, { payload }) => {
        state.recurrencePreviewLoading = false;
        state.recurrencePreview = payload || [];
      })
      .addCase(fetchRecurrencePreview.rejected, (state) => {
        state.recurrencePreviewLoading = false;
      });
  },
});

export const {
  setDateRange,
  setTypeFilter,
  setPlatformFilter,
  setStatusFilter,
  clearCalendarFilters,
  applyOptimisticReschedule,
  revertReschedule,
  toggleSelectMode,
  setSelectMode,
  toggleEventSelection,
  clearSelection,
  setShowBestTimes,
  setSelectedEvent,
  clearSelectedEvent,
  clearRecurrencePreview,
} = contentCalendarSlice.actions;

// Selectors

const selectContentCalendar = (state) => state.contentCalendar;

export const selectCalendarItems = (state) => state.contentCalendar.items;
export const selectCalendarIsLoading = (state) => state.contentCalendar.isLoading;
export const selectCalendarSummary = (state) => state.contentCalendar.summary;
export const selectCalendarWarnings = (state) => state.contentCalendar.warnings;
export const selectCalendarTypeFilter = (state) => state.contentCalendar.typeFilter;
export const selectCalendarPlatformFilter = (state) => state.contentCalendar.platformFilter;
export const selectCalendarStatusFilter = (state) => state.contentCalendar.statusFilter;
export const selectCalendarSelectMode = (state) => state.contentCalendar.selectMode;
export const selectCalendarSelectedIds = (state) => state.contentCalendar.selectedIds;
export const selectShowBestTimes = (state) => state.contentCalendar.showBestTimes;
export const selectCalendarDateRange = (state) => state.contentCalendar.dateRange;
export const selectUpcomingItems = (state) => state.contentCalendar.upcoming;
export const selectUpcomingLoading = (state) => state.contentCalendar.upcomingLoading;
export const selectCalendarStats = (state) => state.contentCalendar.stats;
export const selectSelectedEvent = (state) => state.contentCalendar.selectedEvent;
export const selectRecurrencePreview = (state) => state.contentCalendar.recurrencePreview;
export const selectRecurrencePreviewLoading = (state) => state.contentCalendar.recurrencePreviewLoading;

export const selectCalendarEvents = createSelector([selectCalendarItems], (items) =>
  items.map((item) => ({
    id: item.Id,
    title: item.Title || 'Untitled',
    start: new Date(item.ScheduledAt),
    end: new Date(item.ScheduledAt),
    type: item.Type,
    status: item.Status,
    color: item.Color,
    action: item.Action,
    platforms: item.Platforms,
    thumbnail: item.Thumbnail,
    recurrence: item.Recurrence,
  })),
);

export const selectFilteredCalendarEvents = createSelector(
  [selectCalendarEvents, selectCalendarTypeFilter, selectCalendarPlatformFilter, selectCalendarStatusFilter],
  (events, typeFilter, platformFilter, statusFilter) => {
    const hasType = typeFilter && typeFilter.length > 0;
    const hasPlatform = platformFilter && platformFilter.length > 0;
    const hasStatus = statusFilter && statusFilter.length > 0;
    if (!hasType && !hasPlatform && !hasStatus) return events;

    return events.filter((e) => {
      if (hasType && !typeFilter.includes(e.type)) return false;
      if (hasPlatform) {
        const eventPlatforms = Array.isArray(e.platforms) ? e.platforms.map((p) => String(p).toLowerCase()) : [];
        const match = platformFilter.some((p) => eventPlatforms.includes(String(p).toLowerCase()));
        if (!match) return false;
      }
      if (hasStatus) {
        const status = String(e.status || '').toLowerCase();
        if (!statusFilter.map((s) => s.toLowerCase()).includes(status)) return false;
      }
      return true;
    });
  },
);

export default contentCalendarSlice.reducer;
